import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

interface ReferralStats {
  referralCode?: string;
  totalReferrals: number;
  completedReferrals: number;
  creditedReferrals: number;
  availableCredits: number;
  totalCreditsEarned: number;
  pendingCredits: number;
  referredBy?: string;
}

export const ReferralManagement: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch referral code
      const codeResponse = await fetch(`${apiUrl}/api/referrals/code`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const codeData = await codeResponse.json();

      // Fetch stats
      const statsResponse = await fetch(`${apiUrl}/api/referrals/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const statsData = await statsResponse.json();

      if (statsData.success) {
        setStats(statsData.data);
      } else {
        setError(statsData.message);
      }
    } catch (err) {
      console.error('Fetch referral stats error:', err);
      setError('Failed to load referral information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const copyReferralLink = () => {
    if (stats?.referralCode) {
      const referralUrl = `${window.location.origin}/signup?ref=${stats.referralCode}`;
      navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyReferralCode = () => {
    if (stats?.referralCode) {
      navigator.clipboard.writeText(stats.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-gray-600 dark:text-gray-400">Loading referral information...</div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-error-800 dark:text-error-200">{error || 'Failed to load referral information'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Referral Code Card */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Refer & Earn $50
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Share your referral code with colleagues and earn $50 in credits for each person who upgrades to Premium!
        </p>

        <div className="bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border-2 border-blue-200 dark:border-accent-800 rounded-lg p-4 mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Your Referral Code
          </label>
          <div className="flex items-center space-x-2">
            <div className="flex-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg px-4 py-3">
              <code className="text-2xl font-bold text-accent-600 dark:text-accent-400 tracking-wider">
                {stats.referralCode || 'LOADING...'}
              </code>
            </div>
            <button
              onClick={copyReferralCode}
              className="px-4 py-3 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors font-medium"
            >
              {copied ? '✓ Copied!' : 'Copy Code'}
            </button>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-600 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Or Share This Link
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              readOnly
              value={`${window.location.origin}/signup?ref=${stats.referralCode}`}
              className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-500 rounded-md text-sm text-gray-700 dark:text-gray-300"
            />
            <button
              onClick={copyReferralLink}
              className="px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-md hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
            >
              {copied ? '✓' : 'Copy Link'}
            </button>
          </div>
        </div>
      </div>

      {/* Credits Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Available Credits</h3>
          <p className="text-3xl font-bold text-success-600 dark:text-success-400">
            ${(stats.availableCredits / 100).toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Ready to use on your subscription
          </p>
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Pending Credits</h3>
          <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">
            ${(stats.pendingCredits / 100).toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Waiting for referral to upgrade
          </p>
        </div>

        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Total Earned</h3>
          <p className="text-3xl font-bold text-accent-600 dark:text-accent-400">
            ${(stats.totalCreditsEarned / 100).toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            All-time credits earned
          </p>
        </div>
      </div>

      {/* Referral Stats */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-600">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Referral Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-600 rounded-lg">
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.totalReferrals}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Referrals</p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-600 rounded-lg">
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.completedReferrals}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Signed Up</p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-600 rounded-lg">
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.creditedReferrals}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Upgraded to Premium</p>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6 border border-accent-200 dark:border-accent-800">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">How Referrals Work</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-accent-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
            <p className="text-gray-700 dark:text-gray-300">Share your referral code or link with colleagues</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-accent-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
            <p className="text-gray-700 dark:text-gray-300">They sign up using your referral code</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-accent-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
            <p className="text-gray-700 dark:text-gray-300">When they upgrade to Premium, you get $50 in credits!</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="flex-shrink-0 w-6 h-6 bg-success-600 text-white rounded-full flex items-center justify-center text-sm font-bold">✓</span>
            <p className="text-gray-700 dark:text-gray-300">Credits automatically apply to your next subscription payment</p>
          </div>
        </div>
      </div>

      {/* You Were Referred */}
      {stats.referredBy && (
        <div className="bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-success-800 dark:text-success-200">
            ✓ You were referred using code <strong>{stats.referredBy}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

